import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { format, parse } from 'date-fns';
import {
  isTimeRangeWithinGrid,
  layoutPreviewPixels,
  pointerYToGridTime,
} from './calendarDragTime';
import CalendarMonthRescheduleDialog from './CalendarMonthRescheduleDialog';
import {
  checkCalendarDropAvailabilityForItem,
  isSameCalendarDrop,
  rescheduleCalendarItem,
} from './rescheduleCalendarItem';
import { computeAppointmentDurationMinutes } from '../../shared/booking/double-booking/employeeSlotAvailability';
import { resolveAppointmentEmployeeId } from '../utils/normalizeCompanyAppointment';
import { resolveItemLabelStyle } from '../utils/pastelFromSeed';

const DRAG_THRESHOLD_PX = 6;

const CalendarDragDropContext = createContext(null);

function findColumnElement(startEl) {
  let node = startEl;
  while (node && node !== document.body) {
    if (node.dataset?.teamCalColumnId) return node;
    node = node.parentElement;
  }
  return null;
}

function findGridBodyElement(startEl) {
  let node = startEl;
  while (node && node !== document.body) {
    if (node.dataset?.teamCalGridBody === 'true') return node;
    node = node.parentElement;
  }
  return null;
}

function findMonthCellElement(startEl) {
  let node = startEl;
  while (node && node !== document.body) {
    if (node.dataset?.teamCalMonthDay) return node;
    node = node.parentElement;
  }
  return null;
}

function readGridBodyMetrics(bodyEl) {
  const dayKey = bodyEl.dataset.teamCalDay;
  const gridStartMs = Number(bodyEl.dataset.teamCalGridStartMs);
  const gridEndMs = Number(bodyEl.dataset.teamCalGridEndMs);
  if (!dayKey || !Number.isFinite(gridStartMs) || !Number.isFinite(gridEndMs)) {
    return null;
  }
  return {
    dayKey,
    gridStart: new Date(gridStartMs),
    gridEnd: new Date(gridEndMs),
    bodyEl,
  };
}

function parseMonthDayKey(dayKey) {
  if (!dayKey) return null;
  const parsed = parse(dayKey, 'yyyy-MM-dd', new Date());
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function CalendarDragDropProvider({
  children,
  companyId,
  companyData,
  companyHours,
  user,
  canDragGrid = false,
  canDragMonth = false,
  onDropResult,
}) {
  const [dragState, setDragState] = useState(null);
  const [dropPreview, setDropPreview] = useState(null);
  const [dropAvailability, setDropAvailability] = useState(null);
  const [monthDropPreview, setMonthDropPreview] = useState(null);
  const [monthReschedulePrompt, setMonthReschedulePrompt] = useState(null);
  const pendingRef = useRef(null);
  const dragRef = useRef(null);
  const availabilityTimerRef = useRef(null);
  const availabilityRequestRef = useRef(0);

  const clearAvailabilityTimer = useCallback(() => {
    if (availabilityTimerRef.current) {
      clearTimeout(availabilityTimerRef.current);
      availabilityTimerRef.current = null;
    }
  }, []);

  const resetDrag = useCallback(() => {
    clearAvailabilityTimer();
    pendingRef.current = null;
    dragRef.current = null;
    availabilityRequestRef.current += 1;
    setDragState(null);
    setDropPreview(null);
    setDropAvailability(null);
    setMonthDropPreview(null);
  }, [clearAvailabilityTimer]);

  const scheduleAvailabilityCheck = useCallback(
    (nextPreview, appointment) => {
      if (!companyId || !appointment || !nextPreview?.targetEmployeeId || !nextPreview?.newStartTime) {
        setDropAvailability(null);
        return;
      }

      clearAvailabilityTimer();
      availabilityTimerRef.current = setTimeout(async () => {
        const requestId = availabilityRequestRef.current + 1;
        availabilityRequestRef.current = requestId;
        setDropAvailability({ checking: true, isAvailable: null, reason: null });

        try {
          const result = await checkCalendarDropAvailabilityForItem({
            companyId,
            companyData,
            appointment,
            targetEmployeeId: nextPreview.targetEmployeeId,
            newStartTime: nextPreview.newStartTime,
          });
          if (availabilityRequestRef.current !== requestId) return;
          setDropAvailability({
            checking: false,
            isAvailable: result.isAvailable,
            reason: result.reason,
          });
        } catch (err) {
          if (availabilityRequestRef.current !== requestId) return;
          setDropAvailability({
            checking: false,
            isAvailable: false,
            reason: err.message || 'Unable to check availability.',
          });
        }
      }, 120);
    },
    [clearAvailabilityTimer, companyData, companyId]
  );

  const updateGridDropTargetFromPointer = useCallback(
    (clientX, clientY) => {
      const activeDrag = dragRef.current;
      if (!activeDrag || activeDrag.mode !== 'grid') return;

      const hit = document.elementFromPoint(clientX, clientY);
      const bodyEl = hit ? findGridBodyElement(hit) : null;
      const columnEl = hit ? findColumnElement(hit) : null;

      if (!bodyEl || !columnEl) {
        setDropPreview(null);
        setDropAvailability(null);
        clearAvailabilityTimer();
        return;
      }

      const metrics = readGridBodyMetrics(bodyEl);
      if (!metrics) {
        setDropPreview(null);
        setDropAvailability(null);
        clearAvailabilityTimer();
        return;
      }

      const targetEmployeeId = columnEl.dataset.teamCalColumnId;
      const durationMinutes = computeAppointmentDurationMinutes(activeDrag.item);
      const newStartTime = pointerYToGridTime(clientY, bodyEl, metrics.gridStart, metrics.gridEnd);

      if (!isTimeRangeWithinGrid(newStartTime, durationMinutes, metrics.gridStart, metrics.gridEnd)) {
        setDropPreview(null);
        setDropAvailability(null);
        clearAvailabilityTimer();
        return;
      }

      const previewLayout = layoutPreviewPixels(
        newStartTime,
        durationMinutes,
        metrics.gridStart,
        metrics.gridEnd
      );

      if (!previewLayout) {
        setDropPreview(null);
        setDropAvailability(null);
        clearAvailabilityTimer();
        return;
      }

      const nextPreview = {
        dayKey: metrics.dayKey,
        targetEmployeeId,
        targetEmployeeName: columnEl.dataset.teamCalColumnName || '',
        newStartTime,
        topPx: previewLayout.topPx,
        heightPx: previewLayout.heightPx,
      };

      const prev = activeDrag.lastPreview;
      const changed =
        !prev ||
        prev.dayKey !== nextPreview.dayKey ||
        prev.targetEmployeeId !== nextPreview.targetEmployeeId ||
        prev.newStartTime?.getTime?.() !== nextPreview.newStartTime?.getTime?.();

      activeDrag.lastPreview = nextPreview;
      setDropPreview(nextPreview);

      if (changed) {
        scheduleAvailabilityCheck(nextPreview, activeDrag.item);
      }
    },
    [clearAvailabilityTimer, scheduleAvailabilityCheck]
  );

  const updateMonthDropTargetFromPointer = useCallback((clientX, clientY) => {
    const activeDrag = dragRef.current;
    if (!activeDrag || activeDrag.mode !== 'month') return;

    const hit = document.elementFromPoint(clientX, clientY);
    const cellEl = hit ? findMonthCellElement(hit) : null;
    if (!cellEl) {
      setMonthDropPreview(null);
      activeDrag.lastMonthPreview = null;
      return;
    }

    const dayKey = cellEl.dataset.teamCalMonthDay;
    const targetDay = parseMonthDayKey(dayKey);
    if (!dayKey || !targetDay) {
      setMonthDropPreview(null);
      activeDrag.lastMonthPreview = null;
      return;
    }

    activeDrag.lastMonthPreview = { dayKey, targetDay };
    setMonthDropPreview({ dayKey });
  }, []);

  const updateDropTargetFromPointer = useCallback(
    (clientX, clientY) => {
      const activeDrag = dragRef.current;
      if (!activeDrag) return;
      if (activeDrag.mode === 'month') {
        updateMonthDropTargetFromPointer(clientX, clientY);
      } else {
        updateGridDropTargetFromPointer(clientX, clientY);
      }
    },
    [updateGridDropTargetFromPointer, updateMonthDropTargetFromPointer]
  );

  const beginDrag = useCallback(
    (item, sourceColumnId, event, cardRect) => {
      if (!canDragGrid || item?.type === 'meeting') return;

      pendingRef.current = {
        mode: 'grid',
        item,
        sourceColumnId,
        startX: event.clientX,
        startY: event.clientY,
        pointerId: event.pointerId,
        cardRect,
      };
    },
    [canDragGrid]
  );

  const beginMonthDrag = useCallback(
    (item, event, cardRect) => {
      if (!canDragMonth || item?.type === 'meeting') return;
      if (!resolveAppointmentEmployeeId(item)) return;

      pendingRef.current = {
        mode: 'month',
        item,
        sourceDayKey: item?.dateTime ? format(item.dateTime, 'yyyy-MM-dd') : null,
        startX: event.clientX,
        startY: event.clientY,
        pointerId: event.pointerId,
        cardRect,
      };
    },
    [canDragMonth]
  );

  const commitGridDrop = useCallback(async () => {
    const activeDrag = dragRef.current;
    const preview = activeDrag?.lastPreview;
    if (!activeDrag || !preview || !companyId) {
      const reason = preview ? 'Missing company context.' : 'Drop the appointment on an open time slot.';
      resetDrag();
      onDropResult?.({ ok: false, reason });
      return { ok: false, reason };
    }

    if (
      isSameCalendarDrop(
        activeDrag.item,
        activeDrag.sourceColumnId,
        preview.targetEmployeeId,
        preview.newStartTime
      )
    ) {
      resetDrag();
      return { ok: true, noop: true };
    }

    try {
      const availability = await checkCalendarDropAvailabilityForItem({
        companyId,
        companyData,
        appointment: activeDrag.item,
        targetEmployeeId: preview.targetEmployeeId,
        newStartTime: preview.newStartTime,
      });

      if (!availability.isAvailable) {
        const reason = availability.reason || 'That time slot is not available.';
        resetDrag();
        onDropResult?.({ ok: false, reason });
        return { ok: false, reason };
      }

      await rescheduleCalendarItem({
        companyId,
        appointment: activeDrag.item,
        newStartTime: preview.newStartTime,
        targetEmployeeId: preview.targetEmployeeId,
        targetEmployeeName: preview.targetEmployeeName,
        user,
      });

      resetDrag();
      onDropResult?.({ ok: true, newStartTime: preview.newStartTime });
      return { ok: true };
    } catch (err) {
      const reason = err.message || 'Failed to reschedule appointment.';
      resetDrag();
      onDropResult?.({ ok: false, reason });
      return { ok: false, reason };
    }
  }, [companyData, companyId, onDropResult, resetDrag, user]);

  const commitMonthDrop = useCallback(() => {
    const activeDrag = dragRef.current;
    const preview = activeDrag?.lastMonthPreview;

    if (!activeDrag || !preview?.targetDay) {
      const reason = 'Drop the appointment on another day in the month.';
      resetDrag();
      onDropResult?.({ ok: false, reason });
      return { ok: false, reason };
    }

    if (preview.dayKey === activeDrag.sourceDayKey) {
      resetDrag();
      return { ok: true, noop: true };
    }

    setMonthReschedulePrompt({
      appointment: activeDrag.item,
      targetDay: preview.targetDay,
    });
    resetDrag();
    return { ok: true, pendingTimeSelection: true };
  }, [onDropResult, resetDrag]);

  const commitDrop = useCallback(async () => {
    const activeDrag = dragRef.current;
    if (!activeDrag) return { ok: false };
    if (activeDrag.mode === 'month') {
      return commitMonthDrop();
    }
    return commitGridDrop();
  }, [commitGridDrop, commitMonthDrop]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const pending = pendingRef.current;
      const activeDrag = dragRef.current;

      if (pending && !activeDrag) {
        const dx = event.clientX - pending.startX;
        const dy = event.clientY - pending.startY;
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

        dragRef.current = {
          mode: pending.mode,
          item: pending.item,
          sourceColumnId: pending.sourceColumnId,
          sourceDayKey: pending.sourceDayKey,
          pointerId: pending.pointerId,
          offsetX: pending.startX - pending.cardRect.left,
          offsetY: pending.startY - pending.cardRect.top,
          lastPreview: null,
          lastMonthPreview: null,
        };
        pendingRef.current = null;

        setDragState({
          item: dragRef.current.item,
          mode: dragRef.current.mode,
          sourceColumnId: dragRef.current.sourceColumnId,
          sourceDayKey: dragRef.current.sourceDayKey,
          pointerId: dragRef.current.pointerId,
          ghostLeft: event.clientX - dragRef.current.offsetX,
          ghostTop: event.clientY - dragRef.current.offsetY,
        });
        updateDropTargetFromPointer(event.clientX, event.clientY);
        return;
      }

      if (!activeDrag || event.pointerId !== activeDrag.pointerId) return;

      setDragState((prev) =>
        prev
          ? {
              ...prev,
              ghostLeft: event.clientX - activeDrag.offsetX,
              ghostTop: event.clientY - activeDrag.offsetY,
            }
          : prev
      );
      updateDropTargetFromPointer(event.clientX, event.clientY);
    };

    const handlePointerUp = async (event) => {
      const pending = pendingRef.current;
      const activeDrag = dragRef.current;

      if (pending && event.pointerId === pending.pointerId) {
        pendingRef.current = null;
        return;
      }

      if (!activeDrag || event.pointerId !== activeDrag.pointerId) return;

      event.preventDefault();
      await commitDrop();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [commitDrop, updateDropTargetFromPointer]);

  useEffect(() => () => clearAvailabilityTimer(), [clearAvailabilityTimer]);

  const value = useMemo(
    () => ({
      canDrag: canDragGrid || canDragMonth,
      canDragGrid,
      canDragMonth,
      dragState,
      dropPreview,
      dropAvailability,
      monthDropPreview,
      beginDrag,
      beginMonthDrag,
      cancelDrag: resetDrag,
      getSourceEmployeeId: (item) => resolveAppointmentEmployeeId(item),
    }),
    [
      beginDrag,
      beginMonthDrag,
      canDragGrid,
      canDragMonth,
      dragState,
      dropAvailability,
      dropPreview,
      monthDropPreview,
      resetDrag,
    ]
  );

  return (
    <CalendarDragDropContext.Provider value={value}>
      {children}
      {dragState ? (
        <div
          className="team-cal-v3__drag-ghost"
          style={{
            ...resolveItemLabelStyle(dragState.item),
            left: dragState.ghostLeft,
            top: dragState.ghostTop,
            width: dragState.item?.__dragWidth,
          }}
          aria-hidden
        >
          <span className="team-cal-v3__drag-ghost-accent" aria-hidden />
          <div className="team-cal-v3__drag-ghost-body">
            <div className="team-cal-v3__drag-ghost-title">
              {dragState.item?.clientName || 'Appointment'}
            </div>
            <div className="team-cal-v3__drag-ghost-meta">
              {dragState.item?.serviceName || dragState.item?.status || ''}
            </div>
          </div>
        </div>
      ) : null}
      {monthReschedulePrompt ? (
        <CalendarMonthRescheduleDialog
          open
          appointment={monthReschedulePrompt.appointment}
          targetDay={monthReschedulePrompt.targetDay}
          companyId={companyId}
          companyData={companyData}
          companyHours={companyHours}
          user={user}
          onClose={() => setMonthReschedulePrompt(null)}
          onComplete={onDropResult}
        />
      ) : null}
    </CalendarDragDropContext.Provider>
  );
}

export function useCalendarDragDrop() {
  return useContext(CalendarDragDropContext);
}

export function formatDropPreviewTime(date) {
  if (!date) return '';
  return format(date, 'h:mm a');
}
